import { memo } from "react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS } from "./utils";
import { getEventYear } from "../shared/dateTime";
import { AmbientGlow, OrganicDivider } from "./decorations";

export const GoldenSerenityOurStory = memo(({ event, config, t }: GoldenSerenityComponentProps) => {
  // Story section shows event story/description only (heroText renders in the hero).
  const storyContent = event.story || event.description;
  if (!storyContent || !(event.showStory ?? true)) return null;

  return (
    <section
      id="story"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamDeep}, ${GS.creamLight}, ${GS.cream})` }}
    >
      <AmbientGlow />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center space-y-10 sm:space-y-14">
        {/* Header */}
        <div>
          <div className="mb-8">
            <OrganicDivider width={80} />
          </div>
          <h2
            className="text-4xl sm:text-6xl font-medium mb-3"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.ourStory")}
          </h2>
          <p
            className="text-sm tracking-wide"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.howItAllStarted")}
          </p>
        </div>

        {/* Story content card */}
        <div className="relative max-w-3xl mx-auto">
          <div
            className="relative p-6 sm:p-12 rounded-2xl border"
            style={{
              background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.creamDeep})`,
              borderColor: "rgba(232,220,200,0.4)",
              boxShadow: "0 8px 32px rgba(139,115,85,0.12)",
            }}
          >
            <div className="space-y-8">
              {storyContent.split("\n\n").map((paragraph, i) => (
                <p
                  key={i}
                  className="text-base sm:text-lg leading-relaxed whitespace-pre-line"
                  style={{ color: config.palette.text, fontFamily: config.typography.body }}
                >
                  {paragraph.trim()}
                </p>
              ))}

              {/* Natural divider */}
              <div className="py-2">
                <OrganicDivider />
              </div>

              {event.date && (
                <p
                  className="text-2xl italic font-medium"
                  style={{ color: GS.gold, fontFamily: config.typography.heading }}
                >
                  {getEventYear(event.date)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

GoldenSerenityOurStory.displayName = "GoldenSerenityOurStory";
